import { getPartner } from "../promotions/partners";

/**
 * Provider-level rules: what the coupon partner in play permits.
 *
 * 🔴 **No rule here tests a `metaId`.** Each one asks that provider's capability profile
 * (`./partners`) and applies a rule that is the same for every provider. Adding a partner is a
 * profile; changing what a capability MEANS is a rule here. Keeping those two separate is what
 * stops the flow re-accumulating `if (isExternalProviderC1(...))`.
 *
 * Pure — arguments in, answer out. Nothing here reads the store.
 */


/**
 * Is this partner one whose offers are fetched and applied THROUGH the partner?
 *
 * 🔴 **The gate for the whole external leg** — the offers fetch, the partner apply, the OTP
 * conversation and the removal unblock all hang off it. A provider that answers `false` runs the
 * ordinary coupon path end to end, which is what an unknown `metaId` gets.
 *
 * Replaces `isExternalLoyaltyProvider(metaId)`.
 */
export const isExternalCouponProvider = (metaId?: string): boolean =>
  getPartner(metaId).isExternalLoyalty;

/**
 * Must a coupon this partner issued be released server-side when it comes off the bill?
 *
 * ⚠️ Answered per PARTNER, and the partner is read off the DISCOUNT (`couponProvider`) before
 * falling back to the active provider — see `resolveDiscountProvider`. A bill can carry a coupon
 * from a partner that is no longer the active one.
 */
export const requiresCouponUnblock = (metaId?: string): boolean =>
  getPartner(metaId).requiresCouponUnblock;

/**
 * The configured partner the customer coupon modal serves, or `undefined` — and then nothing
 * offers the modal. The FIRST with `hasCouponModal`, as v1 took the first external partner.
 *
 * One question for its four web sites: the cart header's icon and the customer modal's row ask
 * whether there is one; the modal and that row's points line ask which. All four must ask it the
 * same way — a gate on one predicate and a pick on another would open the modal on DotPe for a
 * merchant with DotPe and Xeno.
 *
 * `= any` because the apps hand in `businessData.loyaltyProviders` untyped, and inference from
 * `any` falls back to the constraint — `{ metaId }` alone, with no `mid` to fetch by.
 */
export const findCouponModalProvider = <T extends { metaId?: string } = any>(
  loyaltyProviders?: T[] | null,
): T | undefined =>
  (loyaltyProviders ?? []).find(
    (provider) => getPartner(provider?.metaId).hasCouponModal,
  );

/**
 * The partner an already-applied discount belongs to.
 *
 * 🔴 **The discount's own `couponProvider` wins over the active provider.** A bill can carry a
 * coupon from a partner the merchant has since switched away from, and every later call about
 * that coupon — the unblock in particular — has to reach the partner that issued it. Falling
 * straight through to the active provider sends the release to the wrong partner and leaves the
 * customer's coupon blocked forever.
 */
export const resolveDiscountProvider = (
  discount: { couponProvider?: string } | null | undefined,
  activeProviderMetaId?: string,
): string | undefined => discount?.couponProvider || activeProviderMetaId;
